import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import usePath from '../../../components/Hooks/usePath';
import { ClarityEvents } from '../../../lib/clarity/types/clarityEvents.types';
import { captureEvent } from '../../../lib/clarity/utils/captureEvent.utils';
import PATHS from '../../../routes/routes.const';
import useSharedEntitystore from '../../../stores/SharedEntities/sharedEntities.store';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useAuthContext } from '../../login/AuthContext';
import ConnectAccountModal from '../../login/ConnectAccountModal';
import OfflineSyncOrderModal from '../../offline/components/OfflineSyncOrderModal/OfflineSyncOrderModal';
import { isOfflineStatus } from '../../offline/domain/offlineOrder/offlineOrder.types';
import useSyncOfflineOrders from '../../offline/hooks/useSyncOfflineOrder/useSyncOfflineOrder';
import SharedOrderModalContent from '../../shared-entities/SharedOrderModalContent';
import AcceptSuggestionOrderModal from '../accept-suggestion-order/AcceptSuggestionOrderModal';
import { ActionEnum } from '../actions/types/orderActions.types';
import { useOrderActionsContext } from '../contexts/OrderActions.context';
import { isActionToFinishOrApproveOrder, isLostOrder } from '../contexts/order.utils';
import { ContractModal } from '../contract/OrderContractModal/OrderContractModal';
import CopyOrderSuggestedOrDraftModal from '../copy-order/CopyOrderSuggestedOrDraftModal';
import ExportOrderModal from '../export/V2/ExportOrderModal';
import { createExportOrderScope } from '../export/V2/services/exportOrderScope';
import OrderWasUpdatedModal from '../forceUpdate/components/OrderWasUpdatedModal';
import UpdateOrderModal from '../forceUpdate/components/UpdateOrderModal';
import useChangeOrderStatus from '../hooks/useChangeOrderStatus';
import { useGetOrderTotals } from '../hooks/useOrderTotals/order-totals.queries';
import SatisfactionSurveyModal from '../satisfaction-survey/SatisfactionSurveyModal';
import { isActionEligibleForSatisfactionSurvey } from '../satisfaction-survey/satisfactionSurvey.utils';
import useSatisfactionSurveyGate from '../satisfaction-survey/useSatisfactionSurveyGate';
import BackToDraftModal from './BackToDraftModal';
import ChangeOrderStatusModal from './ChangeOrderStatusModal/ChangeOrderStatusModal';
import { getStatusToChangeOrderStatusModalBasedInAction } from './ChangeOrderStatusModal/utils/changeOrderStatusModal.utils';
import ExportVerupCsvModal from './ExportVerupCsvModal';
import FinishOrderModal from './FinishOrderModal';
import OrderPaymentHistory from './OrderPaymentHistoryModal/OrderPaymentHistoryModal';
import OrderPaymentModal from './OrderPaymentModal/OrderPaymentModal';
import { orderPaymentModalViewMapper } from './OrderPaymentModal/enums/OrderModalView.enum';
import OrderRefundPaymentModal from './OrderRefundPaymentModal/OrderRefundPaymentModal';
import SignOrderModal from './SignOrderModal/SignOrderModal';
import { OrderModalsProps } from './orderModals.types';

// TODO: PASSOU DA HORA DE REFATORAR ESSE COMPONENTE, PODERIA FACILMENTE TODA A LÓGICA IR PARA UM HOOK E OS COMPONENTES QUE ESTÃO TODOS NO MESMO NÍVEL IREM PARA DENTRO DE UMA PASTA /components
const OrderModals = ({ isOfflineMode, onSuccessSyncOfflineOrder }: OrderModalsProps) => {
  const { isPath } = usePath();
  const { loggedin } = useAuthContext();
  const { syncOfflineOrder } = useSyncOfflineOrders();
  const { refetch: refetchOrderTabs } = useGetOrderTotals({ isOfflineMode });
  const { hasVerupIntegration } = useApplicationContext();
  const { isOpenLoginModal, handleChangeIsOpenLoginModal } = useSharedEntitystore(
    useShallow((state) => ({
      isOpenLoginModal: state.isOpenLoginModal,
      handleChangeIsOpenLoginModal: state.handleChangeIsOpenLoginModal,
    })),
  );

  const [hasAlreadySeenUpdateOrderModal, setHasAlreadySeenUpdateOrderModal] = useState(false);
  const {
    reloadCurrent,
    orderRaw,
    modalState,
    onCloseModal,
    isOrderOutdated,
    onClickOrderAction,
    hasForceUpdatedOrder,
  } = useOrderActionsContext();

  const isOrdersPage = isPath(PATHS.main.orders.path);
  const { isSubmittingAction, onChangeStatus } = useChangeOrderStatus({
    onSuccess: isOrdersPage
      ? () => {
          reloadCurrent();
          refetchOrderTabs();
        }
      : undefined,
  });

  const orderId = orderRaw?.orderId;
  const brandId = orderRaw?.brandId;

  const onConfirmBackToDraft = () => {
    onClickOrderAction(ActionEnum.BACK_TO_DRAFT, orderRaw!);
    onCloseModal();
  };

  const openVerupModal = async () => {
    await onClickOrderAction(ActionEnum.VERUP_EXPORT_CSV, orderRaw!);
  };

  const handleConfirmFinishOrder = async () => {
    await onChangeStatus({
      action: modalState.modal,
      orderId,
      brandId,
    });
    onCloseModal();
  };

  const onCloseUpdateOrderModal = async () => {
    if (modalState.modal === ActionEnum.OPEN_OUTDATED_ORDER_MODAL) {
      setHasAlreadySeenUpdateOrderModal(true);
    }
    onCloseModal();
  };

  const handleSyncOfflineOrder = async () => {
    if (!orderRaw?.orderId) {
      return;
    }
    await syncOfflineOrder(orderRaw?.orderId);
    onCloseModal();
    onSuccessSyncOfflineOrder?.();
  };

  const handleChangeOrderStatusModal = async (reason?: string) => {
    await onChangeStatus({
      reason,
      orderId,
      brandId,
      action: modalState.modal,
    });
    onCloseModal();
  };

  const targetStatusToChangeOrderStatusModal = useMemo(() => {
    return getStatusToChangeOrderStatusModalBasedInAction(modalState.modal);
  }, [modalState.modal]);

  const isFinishingOrder = !!modalState.modal && isActionToFinishOrApproveOrder(modalState.modal);

  const isEligibleFinishAction =
    modalState.show &&
    !!modalState.modal &&
    isActionEligibleForSatisfactionSurvey(modalState.modal) &&
    !hasForceUpdatedOrder &&
    !isOrderOutdated;

  const {
    shouldShowSurvey,
    isSurveyPending,
    survey,
    onAnswerSurvey,
    onContinue,
    onCancel: onCancelSurvey,
  } = useSatisfactionSurveyGate({
    orderId,
    customerId: orderRaw?.customerId,
    isFinishingOrder: isEligibleFinishAction,
    isOfflineMode,
    onCancelFinish: onCloseModal,
  });

  const exportOrderScope = createExportOrderScope({ type: 'ids', ids: [orderId!] });

  return (
    <>
      <OfflineSyncOrderModal
        onClose={onCloseModal}
        onSync={handleSyncOfflineOrder}
        orderCode={orderRaw?.orderCode}
        isOpen={modalState.show && modalState.modal === ActionEnum.SYNC_OFFLINE_ORDER}
      />

      <ExportOrderModal
        isOfflineMode={isOfflineStatus(orderRaw?.orderStatus)}
        open={modalState.show && modalState.modal === ActionEnum.EXPORT_ORDER}
        onClose={onCloseModal}
        openVerupModal={openVerupModal}
        exportScope={exportOrderScope}
      />

      {!isOfflineMode && (
        <>
          <ContractModal
            open={
              modalState.show &&
              (modalState.modal === ActionEnum.OPEN_CONTRACT_MODAL ||
                modalState.modal === ActionEnum.VIEW_CONTRACTS)
            }
            onClose={onCloseModal}
            orderId={orderId}
            isReadOnly={isLostOrder(orderRaw?.orderStatus)}
          />

          <BackToDraftModal
            open={modalState.show && modalState.modal === ActionEnum.CONFIRM_BACK_TO_DRAFT}
            onClose={onCloseModal}
            onConfirm={onConfirmBackToDraft}
          />

          {!loggedin && (
            <ConnectAccountModal
              open={isOpenLoginModal}
              onClose={() => handleChangeIsOpenLoginModal(false)}
            />
          )}

          {orderId && orderRaw.orderCode && (
            <SharedOrderModalContent
              isOpen={modalState.show && modalState.modal === ActionEnum.SHARE_ORDER}
              onClose={onCloseModal}
              status={isOfflineStatus(orderRaw.orderStatus) ? undefined : orderRaw?.orderStatus}
              orderId={orderId}
              orderCode={orderRaw.orderCode}
            />
          )}

          {hasVerupIntegration && (
            <ExportVerupCsvModal
              open={modalState.show && modalState.modal === ActionEnum.VERUP_EXPORT_CSV}
              onClose={onCloseModal}
            />
          )}

          {orderRaw?.orderId && orderRaw.orderCode && (
            <>
              <OrderPaymentModal
                onClose={onCloseModal}
                orderId={orderRaw.orderId}
                orderCode={orderRaw.orderCode}
                isOpen={
                  !!(
                    modalState.show &&
                    modalState.modal &&
                    [
                      ActionEnum.OPEN_ORDER_PAYMENT_MODAL,
                      ActionEnum.OPEN_CANCEL_PAYMENT_LINK_MODAL,
                    ].includes(modalState.modal)
                  )
                }
                defaultView={
                  modalState.modal &&
                  (modalState.modal === ActionEnum.OPEN_ORDER_PAYMENT_MODAL ||
                    modalState.modal === ActionEnum.OPEN_CANCEL_PAYMENT_LINK_MODAL)
                    ? orderPaymentModalViewMapper[modalState.modal]
                    : undefined
                }
              />

              <OrderPaymentHistory
                setClose={onCloseModal}
                orderId={orderRaw.orderId}
                isOpen={
                  modalState.show &&
                  modalState.modal === ActionEnum.OPEN_ORDER_PAYMENT_HISTORY_MODAL
                }
              />

              <OrderRefundPaymentModal
                onSuccess={reloadCurrent}
                orderCode={orderRaw.orderCode}
                onClose={onCloseModal}
                orderId={orderRaw.orderId}
                isOpen={
                  modalState.show && modalState.modal === ActionEnum.OPEN_ORDER_REFUND_PAYMENT_MODAL
                }
              />
            </>
          )}

          {orderId && orderRaw.orderCode && (
            <>
              {orderRaw && targetStatusToChangeOrderStatusModal && (
                <ChangeOrderStatusModal
                  onClose={onCloseModal}
                  isOpen={modalState.show}
                  onConfirm={handleChangeOrderStatusModal}
                  orderParams={{
                    code: orderRaw.orderCode,
                    targetStatus: targetStatusToChangeOrderStatusModal,
                  }}
                />
              )}

              <CopyOrderSuggestedOrDraftModal
                onClose={onCloseModal}
                reload={reloadCurrent}
                type={ActionEnum.COPY_AS_DRAFT}
                isOpen={
                  modalState.show && modalState.modal === ActionEnum.COPY_AS_DRAFT && loggedin
                }
              />

              <CopyOrderSuggestedOrDraftModal
                isOpen={modalState.show && modalState.modal === ActionEnum.COPY_AS_SUGGESTED}
                onClose={onCloseModal}
                reload={reloadCurrent}
                type={ActionEnum.COPY_AS_SUGGESTED}
              />

              <AcceptSuggestionOrderModal
                orderCode={orderRaw.orderCode}
                onClose={onCloseModal}
                isOpen={
                  modalState.show &&
                  modalState.modal === ActionEnum.ACCEPT_SUGGESTION &&
                  !hasForceUpdatedOrder &&
                  !isOrderOutdated
                }
              />

              <UpdateOrderModal
                orderId={orderId}
                onClose={onCloseModal}
                onCloseWithAlreadySeenUpdate={onCloseUpdateOrderModal}
                isOrderOutdated={isOrderOutdated}
                isFinishingOrder={isFinishingOrder}
                handleConfirmFinishOrder={handleConfirmFinishOrder}
                resetHasAlreadySeenUpdate={() => setHasAlreadySeenUpdateOrderModal(false)}
                isOpen={
                  modalState.show &&
                  !!modalState.modal &&
                  (modalState.modal === ActionEnum.OPEN_UPDATE_ORDER_MODAL ||
                    (modalState.modal === ActionEnum.ACCEPT_SUGGESTION && isOrderOutdated) ||
                    (isActionToFinishOrApproveOrder(modalState.modal) && isOrderOutdated) ||
                    (modalState.modal === ActionEnum.OPEN_OUTDATED_ORDER_MODAL &&
                      !hasAlreadySeenUpdateOrderModal))
                }
              />

              <OrderWasUpdatedModal
                onClose={onCloseModal}
                isFinishingOrder={isFinishingOrder}
                handleConfirmFinishOrder={handleConfirmFinishOrder}
                isOpen={
                  modalState.show &&
                  !!modalState.modal &&
                  (modalState.modal === ActionEnum.OPEN_ORDER_WAS_UPDATED_MODAL ||
                    (modalState.modal === ActionEnum.ACCEPT_SUGGESTION && hasForceUpdatedOrder) ||
                    (isActionToFinishOrApproveOrder(modalState.modal) && hasForceUpdatedOrder))
                }
              />

              <FinishOrderModal
                orderId={orderId}
                onClose={onCloseModal}
                orderCode={orderRaw?.orderCode}
                customerCommercialName={orderRaw?.customerCommercialName}
                onConfirm={async () => {
                  captureEvent(ClarityEvents.ORDER_SCREEN_CLICKED_FINISH_ORDER);
                  await handleConfirmFinishOrder();
                }}
                isSubmittingAction={isSubmittingAction}
                action={isFinishingOrder ? modalState.modal : undefined}
                open={
                  modalState.show &&
                  isFinishingOrder &&
                  !hasForceUpdatedOrder &&
                  !isOrderOutdated &&
                  !shouldShowSurvey &&
                  !isSurveyPending
                }
                orderStatus={
                  isOfflineStatus(orderRaw?.orderStatus) ? undefined : orderRaw?.orderStatus
                }
              />

              <SatisfactionSurveyModal
                isOpen={shouldShowSurvey}
                survey={survey}
                onAnswerSurvey={onAnswerSurvey}
                onContinue={onContinue}
                onCancel={onCancelSurvey}
              />

              <SignOrderModal
                open={modalState.show && modalState.modal === ActionEnum.OPEN_SIGN_ORDER_MODAL}
                orderId={orderId}
                orderCode={orderRaw?.orderCode}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default OrderModals;
